"use client";

import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import useTranslation from "next-translate/useTranslation";
import { LiquidPill } from "./liquid-pill";
import type { CircleActionVariant } from "./circle-action-variants";

type ModalButtonsAction = (event?: any) => void | Promise<unknown>;

export interface ModalButtonsProps {
  loading?: boolean;
  disabled?: boolean;
  cancelLabel?: string;
  submitLabel?: string;
  /** @deprecated use cancelLabel */
  buttonOneText?: string;
  /** @deprecated use submitLabel */
  buttonTwoText?: string;
  /** @deprecated use onCancel */
  closeAction?: () => void;
  /** @deprecated use onCancel */
  buttonOneAction?: ModalButtonsAction;
  /** @deprecated use onSubmit */
  buttonTwoAction?: ModalButtonsAction;
  /** @deprecated use disabled */
  buttonTwoDisabled?: boolean;
  onCancel?: () => void;
  onClose?: () => void;
  onSubmit?: ModalButtonsAction;
  submitVariant?: "primary" | "destructive" | "success" | "warning";
  /** @deprecated use submitVariant */
  variant?: string;
  compact?: boolean;
  className?: string;
  submitButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

function submitPillVariant(
  submitVariant: ModalButtonsProps["submitVariant"]
): CircleActionVariant {
  switch (submitVariant) {
    case "destructive":
      return "rose";
    case "success":
      return "emerald";
    case "warning":
      return "gold";
    case "primary":
    default:
      return "indigo";
  }
}

export function ModalButtons({
  loading = false,
  disabled = false,
  cancelLabel,
  submitLabel,
  buttonOneText,
  buttonTwoText,
  closeAction,
  buttonOneAction,
  buttonTwoAction,
  buttonTwoDisabled,
  onCancel,
  onClose,
  onSubmit,
  submitVariant = "primary",
  compact = false,
  className,
  submitButtonProps,
}: ModalButtonsProps) {
  const resolvedSubmitLabel = submitLabel || buttonTwoText;
  const resolvedOnCancel =
    onCancel ?? onClose ?? buttonOneAction ?? closeAction ?? (() => {});
  const resolvedOnSubmit = onSubmit ?? buttonTwoAction;
  const { t } = useTranslation("common");
  const pillSize = "md";
  const submitDisabled = loading || disabled || Boolean(buttonTwoDisabled);
  const formId =
    typeof submitButtonProps?.form === "string"
      ? submitButtonProps.form
      : undefined;

  const handleSubmit: React.MouseEventHandler<HTMLElement> = (event) => {
    submitButtonProps?.onClick?.(
      event as unknown as React.MouseEvent<HTMLButtonElement>
    );
    if (event.defaultPrevented) return;

    if (resolvedOnSubmit) {
      void resolvedOnSubmit(event);
      return;
    }

    const form =
      (formId ? document.getElementById(formId) : null) ??
      (event.currentTarget as HTMLElement).closest("form");

    if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    }
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end",
        compact ? "pt-0" : "pt-6",
        className
      )}
    >
      <LiquidPill
        as="button"
        type="button"
        variant="slate"
        appearance="tinted"
        surfaceStyle="selectionSolid"
        size={pillSize}
        disabled={loading}
        text={cancelLabel || buttonOneText || t("Cancelar")}
        onClick={resolvedOnCancel}
        className="justify-center sm:min-w-[6.5rem]"
      />
      <LiquidPill
        as="button"
        type="button"
        variant={submitPillVariant(submitVariant)}
        appearance="tinted"
        surfaceStyle="selectionSolid"
        size={pillSize}
        disabled={submitDisabled}
        form={formId}
        icon={
          loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : undefined
        }
        text={resolvedSubmitLabel || t("Guardar")}
        onClick={handleSubmit}
        aria-label={submitButtonProps?.["aria-label"]}
        className={cn("justify-center sm:min-w-[6.5rem]", submitButtonProps?.className)}
      />
    </div>
  );
}
